import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { 
  BookOpen, 
  Users, 
  Clock, 
  Star, 
  CheckCircle, 
  Play,
  Calendar,
  Award,
  Brain,
  Heart,
  Target,
  ArrowRight,
  Download,
  Video,
  FileText,
  Shield,
  Zap,
  TrendingUp
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEO/SEOHead';
import { courses, courseTestimonials, learningBenefits, courseUtils } from '../data/courses';

export default function CoursesPage() {
  const { t, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  const [selectedCourse, setSelectedCourse] = useState(null);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  // Получаем активные курсы
  const activeCourses = courseUtils.getActiveCourses();
  const featuredCourses = courseUtils.getFeaturedCourses();

  // Функция для получения иконки по имени
  const getIcon = (iconName) => {
    const icons = {
      Brain, Heart, Target, CheckCircle, Video, FileText, Users, Award, Shield, Zap, TrendingUp
    };
    return icons[iconName] || Brain;
  };

  return (
    <div className="min-h-screen bg-white text-dark-900">
      <SEOHead 
        title={t('courses.seo.title')}
        description={t('courses.seo.description')}
        keywords={t('courses.seo.keywords')}
      />
      <Header />
      <main>
        {/* Преимущества обучения */}
        <section className="py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 text-dark-900">
                Почему наши курсы эффективны
              </motion.h2>
              <motion.p variants={item} className="text-body max-w-3xl mx-auto">
                Мы используем проверенные методики и современные технологии обучения
              </motion.p>
            </motion.div>

             <motion.div
               variants={container}
               initial="hidden"
               whileInView="show"
               viewport={{ once: true }}
               className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
             >
               {learningBenefits.map((benefit, index) => {
                 const IconComponent = getIcon(benefit.icon);
                 return (
                   <motion.div
                     key={index}
                     variants={item}
                     className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:scale-105 text-center"
                   >
                     <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                       <IconComponent className="w-8 h-8 text-rose-600" />
                     </div>
                     <h3 className="text-lg font-semibold mb-2 text-dark-900">{benefit.title}</h3>
                     <p className="text-gray-600 text-sm">{benefit.description}</p>
                   </motion.div>
                 );
               })}
             </motion.div>
          </div>
        </section>

        {/* Курсы */}
        <section className="py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 text-dark-900">
                Доступные курсы
              </motion.h2>
              <motion.p variants={item} className="text-body max-w-3xl mx-auto">
                Выберите курс, который подходит вашему уровню и целям
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid lg:grid-cols-3 gap-8 mb-16"
            >
              {activeCourses.map((course, index) => (
                <motion.div
                  key={course.id}
                  variants={item}
                  className="card-elevated p-8 relative overflow-hidden"
                >

                  {/* Заголовок и описание */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-3 text-dark-900">{course.title}</h3>
                    <p className="text-gray-600 mb-4">{course.description}</p>
                  </div>

                  {/* Информация о курсе */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BookOpen className="w-4 h-4" />
                      <span>Уровень: {course.level}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{course.students} студентов</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span>{course.rating} рейтинг</span>
                    </div>
                  </div>

                  {/* Особенности */}
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3 text-dark-900">Что включено:</h4>
                    <ul className="space-y-2">
                      {course.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Цена */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-bold text-dark-900">{course.price}</span>
                      <span className="text-lg text-gray-500 line-through">{course.originalPrice}</span>
                    </div>
                  </div>

                  {/* Кнопка */}
                  <button
                    onClick={() => setSelectedCourse(course)}
                    className="w-full btn-primary"
                  >
                    Подробнее о курсе
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Отзывы */}
        <section className="py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 text-dark-900">
                Отзывы студентов
              </motion.h2>
              <motion.p variants={item} className="text-body max-w-3xl mx-auto">
                Что говорят наши студенты о курсах
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8 mb-16"
            >
              {courseTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-dark-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.course}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Призыв к действию */}
        <section className="py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center"
            >
              <motion.div variants={item} className="block-primary text-center">
                <h3 className="heading-3 mb-4">
                  {t('courses.cta.title')}
                </h3>
                <p className="text-body mb-6">
                  {t('courses.cta.description')}
                </p>
                <a
                  href={getLocalizedPath("/contact")}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {t('courses.cta.button')}
                  <ArrowRight className="w-5 h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Модальное окно с деталями курса */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl font-bold text-dark-900">{selectedCourse.title}</h2>
              <button
                onClick={() => setSelectedCourse(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-6">{selectedCourse.description}</p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Длительность: {selectedCourse.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Уровень: {selectedCourse.level}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">{selectedCourse.students} студентов</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="font-medium">Рейтинг: {selectedCourse.rating}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-4">Что включено:</h3>
                  <ul className="space-y-2">
                    {selectedCourse.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Программа курса:</h3>
                <div className="space-y-4">
                  {selectedCourse.modules.map((module, index) => {
                    const ModuleIcon = getIcon(module.icon);
                    return (
                      <div key={module.id} className="card-elevated p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                            <ModuleIcon className="w-5 h-5 text-rose-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{module.title}</h4>
                            <p className="text-sm text-gray-600">{module.duration} • {module.lessons} уроков</p>
                            <p className="text-xs text-gray-500">{module.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 p-6 bg-rose-50 rounded-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-bold text-dark-900">{selectedCourse.price}</span>
                    <span className="text-lg text-gray-500 line-through">{selectedCourse.originalPrice}</span>
                  </div>
                  <button className="w-full btn-primary">
                    Записаться на курс
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
